import { useEffect } from 'react';
import { useUser, useSupabaseClient } from '@supabase/auth-helpers-react';
import * as Sentry from '@sentry/react';
import { useSessionDurationTracking } from './useUserTracking';
import { MixpanelActions } from '../integrations/mixpanel/mixpanel';

/**
 * Hook to sync Supabase auth user with Sentry
 * This will automatically update Sentry user information when a user logs in or out
 */
/**
 * Custom hook that syncs the Supabase authenticated user with Sentry for error tracking
 * 
 * This hook:
 * 1. Sets user information in Sentry when a user is logged in
 * 2. Clears user information when a user logs out
 * 3. Sets up a listener for auth state changes to keep Sentry updated
 * 
 * @returns void
 */
export function useSentryUser() {
  const user = useUser();
  const supabase = useSupabaseClient();
  useSessionDurationTracking(user)
  useEffect(() => {
    // Function to update Sentry user context
    const updateSentryUser = (user: any) => {
      if (user) {
        // Extract user metadata for better context in Sentry
        const userData = {
          id: user.id,
          email: user.email,
          username: user.user_metadata?.username || user.email?.split('@')[0] || undefined,
          name: user.user_metadata?.full_name || user.user_metadata?.name || undefined,
          // Include additional useful metadata
          provider: user.app_metadata?.provider,
          created_at: user.created_at,
          last_sign_in_at: user.last_sign_in_at,
        };
        
        // Set user information in Sentry
        Sentry.setUser(userData);
        console.debug('Sentry user context updated', userData);
      } else {
        // Clear Sentry user information when user logs out
        Sentry.setUser(null);
        console.debug('Sentry user context cleared');
      }
    };

    // Update Sentry with current user state
    updateSentryUser(user);

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.debug('Auth state changed:', event);
      
      if(event === 'SIGNED_IN') {
        MixpanelActions.signedIn(session.user.email)
      }

      updateSentryUser(session?.user);
    });

    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [user, supabase]);

  return null;
}
